import { useState } from 'react';
import { menuApi } from '../../lib/menuApi';
import MenuErrorBanner from './MenuErrorBanner';

const LINES_HINT = 'Ribeye 14oz - 42.00 - Steaks\nHouse Salad - 9.50 - Salads\nIced Tea - 3 - Beverages';
const CSV_HINT = 'name,category,price,description,ingredients,preparation,allergens,temperatures,wine_pairing\n"Ribeye, 14 oz",Steaks,42.00,Bone-in,,Grilled,dairy,Rare;Medium Rare,Cabernet';

/**
 * Paste or CSV-upload import (POST /api/menu/import). Preview is a dry run:
 * per-row errors and the categories it would create; commit is enabled only
 * when every row is valid (the server imports all-or-nothing).
 */
export default function MenuImport({ onImported }) {
    const [format, setFormat] = useState('lines');
    const [text, setText] = useState('');
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(null);

    const reset = () => { setPreview(null); setDone(null); setError(null); };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFormat('csv');
        setText(await file.text());
        reset();
    };

    const run = async (dryRun) => {
        setBusy(true);
        setError(null);
        try {
            const result = await menuApi.importMenu({ format, text, dryRun });
            if (dryRun) setPreview(result);
            else {
                setDone(result);
                setPreview(null);
                setText('');
                onImported?.(result);
            }
        } catch (err) {
            setError(err);
            if (err.body?.rows) setPreview(err.body); // 422: show which rows failed
        } finally {
            setBusy(false);
        }
    };

    const canCommit = preview && preview.valid > 0 && preview.invalid === 0 && !busy;

    return (
        <section aria-labelledby="menu-import-title" className="space-y-4">
            <div>
                <h3 id="menu-import-title" className="text-lg font-bold text-gray-900">Import menu items</h3>
                <p className="text-sm text-gray-600">
                    Paste one item per line as <code>Name - Price - Category</code>, or upload a CSV with a
                    <code> name,category,price</code> header. Up to 500 items. Missing categories are created.
                </p>
            </div>

            <fieldset className="flex flex-wrap gap-4">
                <legend className="sr-only">Import format</legend>
                {[['lines', 'Paste lines'], ['csv', 'CSV']].map(([value, label]) => (
                    <label key={value} className="flex items-center gap-2 text-gray-800">
                        <input type="radio" name="import-format" value={value} checked={format === value}
                            onChange={() => { setFormat(value); reset(); }} />
                        {label}
                    </label>
                ))}
                <label className="flex items-center gap-2 text-gray-800">
                    <span>Upload CSV file</span>
                    <input type="file" accept=".csv,text/csv" onChange={onFile} className="text-sm" aria-label="Upload CSV file" />
                </label>
            </fieldset>

            <label htmlFor="menu-import-text" className="block text-sm font-medium text-gray-700">Menu text</label>
            <textarea
                id="menu-import-text"
                value={text}
                onChange={(e) => { setText(e.target.value); reset(); }}
                rows={8}
                placeholder={format === 'csv' ? CSV_HINT : LINES_HINT}
                className="w-full rounded-lg border border-gray-300 p-3 font-mono text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-400"
            />

            <MenuErrorBanner error={error} />
            {done && (
                <p role="status" className="rounded-lg bg-green-50 border border-green-300 text-green-800 px-4 py-3 text-sm">
                    Imported {done.inserted} item(s){done.categoriesCreated?.length ? ` and created ${done.categoriesCreated.join(', ')}` : ''}.
                </p>
            )}

            <div className="flex flex-wrap gap-3">
                <button type="button" onClick={() => run(true)} disabled={busy || !text.trim()}
                    className="bg-gray-800 hover:bg-gray-900 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-purple-400">
                    {busy && !preview ? 'Checking…' : 'Preview'}
                </button>
                <button type="button" onClick={() => run(false)} disabled={!canCommit}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-purple-400">
                    {preview ? `Import ${preview.valid} item(s)` : 'Import'}
                </button>
            </div>

            {preview && (
                <div className="space-y-2" aria-live="polite">
                    <p className="text-sm text-gray-700">
                        {preview.valid} ready, {preview.invalid} with errors.
                        {preview.newCategories?.length > 0 && ` New categories: ${preview.newCategories.join(', ')}.`}
                        {preview.invalid > 0 && ' Fix the rows with errors and preview again.'}
                    </p>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm bg-white rounded-lg border border-gray-200">
                            <caption className="sr-only">Import preview</caption>
                            <thead className="bg-gray-50 text-left text-gray-600">
                                <tr><th scope="col" className="p-2">Line</th><th scope="col" className="p-2">Name</th>
                                    <th scope="col" className="p-2">Category</th><th scope="col" className="p-2">Price</th>
                                    <th scope="col" className="p-2">Status</th></tr>
                            </thead>
                            <tbody>
                                {preview.rows.map((r) => (
                                    <tr key={r.line} className={r.errors.length ? 'bg-red-50' : ''}>
                                        <td className="p-2">{r.line}</td>
                                        <td className="p-2">{r.item.name}</td>
                                        <td className="p-2">{r.item.category}</td>
                                        <td className="p-2">{typeof r.item.price === 'number' ? `$${r.item.price.toFixed(2)}` : r.item.price}</td>
                                        <td className="p-2">{r.errors.length ? <span className="text-red-700">{r.errors.join('; ')}</span> : <span className="text-green-700">OK</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </section>
    );
}
